import { Alert, Button, FileButton, Group, List, Stack, Text } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconDownload, IconFileSpreadsheet, IconUpload } from '@tabler/icons-react'
import { type OpeningKind, openingColumns, openingRecords } from '@tnn/domain'
import { type ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { exportSheet, parseFile } from './dataio'

export interface SheetImportResult {
  ok: boolean
  /** By data row, from 1 (the header row is not counted) */
  errors: { row: number; message: string }[]
  count: number
}

export interface SheetImportProps {
  kind: OpeningKind
  /** A column's header in the sheet */
  header: (key: string) => string
  /** The template's file name */
  fileName: string
  /** Checks (`dryRun`) or saves the rows: all of them or none */
  send: (rows: Record<string, unknown>[], dryRun: boolean) => Promise<SheetImportResult>
  /** Choices above the file (the tax year) */
  children?: ReactNode
}

/**
 * A sheet in a fixed layout, imported all at once: the template to fill, the file chosen, every
 * row checked by the server first (and shown with what is wrong), then saved together
 */
export function SheetImport({ kind, header, fileName, send, children }: SheetImportProps) {
  const { t } = useTranslation()
  const columns = openingColumns[kind]
  const [rows, setRows] = useState<Record<string, unknown>[] | null>(null)
  const [file, setFile] = useState<string | null>(null)
  const [check, setCheck] = useState<SheetImportResult | null>(null)
  const [busy, setBusy] = useState<'check' | 'save' | null>(null)

  const template = () =>
    exportSheet<Record<string, unknown>>({
      fileName,
      sheet: fileName,
      // One empty row: the spreadsheet library writes the header row only above a row (`downloadTemplate` too)
      rows: [{}],
      columns: columns.map((c) => ({ header: header(c.key), value: () => null, type: c.type === 'number' ? 'money' : c.type })),
    })

  const choose = async (picked: File | null) => {
    if (!picked) return
    setFile(picked.name)
    setCheck(null)
    try {
      const parsed = await parseFile(picked)
      const records = openingRecords(kind, parsed.rows)
      setRows(records)
      setBusy('check')
      setCheck(await send(records, true))
    } catch (error) {
      setRows(null)
      notifications.show({ color: 'red', message: (error as Error).message || t('import.unsupported') })
    } finally {
      setBusy(null)
    }
  }

  const save = async () => {
    if (!rows) return
    setBusy('save')
    try {
      const result = await send(rows, false)
      if (result.ok) {
        notifications.show({ message: t('import.imported', { count: result.count }) })
        setRows(null)
        setFile(null)
        setCheck(null)
      } else setCheck(result)
    } catch (error) {
      notifications.show({ color: 'red', message: (error as Error).message })
    } finally {
      setBusy(null)
    }
  }

  return (
    <Stack gap="md">
      {children}
      <Group gap="sm">
        <Button variant="subtle" leftSection={<IconDownload size={18} />} onClick={template}>
          {t('import.template')}
        </Button>
        <FileButton onChange={choose} accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv">
          {(props) => (
            <Button {...props} variant="light" leftSection={<IconUpload size={18} />} loading={busy === 'check'}>
              {t('import.chooseFile')}
            </Button>
          )}
        </FileButton>
      </Group>
      {file && (
        <Group gap={6}>
          <IconFileSpreadsheet size={18} />
          <Text size="sm">{file}</Text>
          {rows && (
            <Text size="sm" c="dimmed">
              · {t('import.rows', { count: rows.length })}
            </Text>
          )}
        </Group>
      )}
      {check && check.errors.length > 0 && (
        <Alert
          color="warning"
          variant="light"
          radius="md"
          title={t('import.invalid', { count: new Set(check.errors.map((e) => e.row)).size })}
        >
          <Stack gap={6}>
            <List size="sm" spacing={2}>
              {check.errors.slice(0, 50).map((e) => (
                <List.Item key={`${e.row}:${e.message}`}>{`${t('import.row', { row: e.row })}: ${e.message}`}</List.Item>
              ))}
            </List>
            <Text size="xs">{t('import.allOrNothing')}</Text>
          </Stack>
        </Alert>
      )}
      {check?.ok && rows && (
        <Group>
          <Button onClick={save} loading={busy === 'save'}>
            {t('import.importValid', { count: rows.length })}
          </Button>
        </Group>
      )}
    </Stack>
  )
}
